import Server, { Provider } from '../types/Server'
import ServerFilter from '../types/ServerFilter'
import { grossPrice, locationOf, PROVIDER_NAMES } from './servers'

const maxOf = (values: number[]) => (values.length ? Math.max(...values) : 0)
const nextPow2 = (n: number) => 2 ** Math.ceil(Math.log2(Math.max(1, n)))

export function calcInitialFilters(servers: Server[]): ServerFilter {
  return {
    providers: availableProviders(servers),
    cpu: '',
    maxPrice: Math.max(1, Math.ceil(maxOf(servers.map(grossPrice)))),
    locations: [],
    eccOnly: false,

    minRAM: 1,
    maxRAM: nextPow2(maxOf(servers.map((s) => s.ram_size))),

    minNvmeSize: 0,
    maxNvmeSize: maxOf(servers.flatMap((s) => s.serverDiskData.nvme)),
    minNvmeCount: 0,
    maxNvmeCount: maxOf(servers.map((s) => s.serverDiskData.nvme.length)),

    minSataSize: 0,
    maxSataSize: maxOf(servers.flatMap((s) => s.serverDiskData.sata)),
    minSataCount: 0,
    maxSataCount: maxOf(servers.map((s) => s.serverDiskData.sata.length)),

    minHddSize: 0,
    maxHddSize: maxOf(servers.flatMap((s) => s.serverDiskData.hdd)),
    minHddCount: 0,
    maxHddCount: maxOf(servers.map((s) => s.serverDiskData.hdd.length)),
  }
}

export function availableProviders(servers: Server[]): Provider[] {
  const present = new Set(servers.map((s) => s.provider))
  return (Object.keys(PROVIDER_NAMES) as Provider[]).filter((p) => present.has(p))
}

export type ProviderLocations = { provider: Provider; locations: string[] }

export function availableLocations(servers: Server[]): ProviderLocations[] {
  return availableProviders(servers).map((provider) => ({
    provider,
    locations: [
      ...new Set(servers.filter((s) => s.provider === provider).map(locationOf)),
    ].sort(),
  }))
}

export type DiskSizeOptions = { nvme: number[]; sata: number[]; hdd: number[] }

export function availableDiskSizes(servers: Server[]): DiskSizeOptions {
  const uniq = (values: number[]) => [...new Set(values)].sort((a, b) => a - b)
  return {
    nvme: uniq(servers.flatMap((s) => s.serverDiskData.nvme)),
    sata: uniq(servers.flatMap((s) => s.serverDiskData.sata)),
    hdd: uniq(servers.flatMap((s) => s.serverDiskData.hdd)),
  }
}

// "At least minCount disks within [minSize, maxSize]". The size window is
// only enabled in the UI once minCount >= 1, so size and count act as one
// combined constraint here.
function hasMatchingDisks(
  disks: number[],
  minCount: number,
  minSize: number,
  maxSize: number
): boolean {
  return disks.filter((size) => size >= minSize && size <= maxSize).length >= minCount
}

export function applyFilters(servers: Server[], f: ServerFilter): Server[] {
  const cpuQuery = f.cpu.trim().toLowerCase()

  return servers.filter((server) => {
    if (!f.providers.includes(server.provider)) return false
    if (cpuQuery && !server.cpu.toLowerCase().includes(cpuQuery)) return false
    if (f.locations.length > 0 && !f.locations.includes(locationOf(server))) return false
    if (f.eccOnly && !server.is_ecc) return false
    if (grossPrice(server) > f.maxPrice) return false
    if (server.ram_size < f.minRAM || server.ram_size > f.maxRAM) return false

    const disks = server.serverDiskData
    // Max count is an exclusion ("no more than N of this type") and always
    // ANDs; min count + size window is a selection, combined as
    // NVMe OR SATA, AND HDD — mirroring the dividers in the filter UI.
    if (disks.nvme.length > f.maxNvmeCount) return false
    if (disks.sata.length > f.maxSataCount) return false
    if (disks.hdd.length > f.maxHddCount) return false

    if (
      f.minHddCount > 0 &&
      !hasMatchingDisks(disks.hdd, f.minHddCount, f.minHddSize, f.maxHddSize)
    ) {
      return false
    }
    const nvmeSelected = f.minNvmeCount > 0
    const sataSelected = f.minSataCount > 0
    if (nvmeSelected || sataSelected) {
      const nvmeOk =
        nvmeSelected &&
        hasMatchingDisks(disks.nvme, f.minNvmeCount, f.minNvmeSize, f.maxNvmeSize)
      const sataOk =
        sataSelected &&
        hasMatchingDisks(disks.sata, f.minSataCount, f.minSataSize, f.maxSataSize)
      if (!nvmeOk && !sataOk) return false
    }
    return true
  })
}

export function countActiveFilters(f: ServerFilter, initial: ServerFilter): number {
  let count = 0
  if (f.providers.length < initial.providers.length) count++
  if (f.cpu.trim() !== '') count++
  if (f.locations.length > 0) count++
  if (f.eccOnly) count++
  if (f.maxPrice < initial.maxPrice) count++
  if (f.minRAM > initial.minRAM || f.maxRAM < initial.maxRAM) count++
  if (f.minNvmeCount > 0 || f.maxNvmeCount < initial.maxNvmeCount) count++
  if (f.minSataCount > 0 || f.maxSataCount < initial.maxSataCount) count++
  if (f.minHddCount > 0 || f.maxHddCount < initial.maxHddCount) count++
  return count
}
